// Owns the VM: fixed 60 Hz ticks, host commands, and the rewind history.
import { prepare, setBeat, setField, setVar } from './setup.ts';
import {
  type Bullets,
  type EvalResult,
  init,
  type Lasers,
  type LoadResult,
  type SpawnResult,
  simVersion,
  Vm,
  type VmEvent,
} from './vm.ts';

export type { Bullets, EvalResult, Lasers, LoadResult, SourceError, SpawnResult, VmEvent } from './vm.ts';

/**
 * The playfield. The height is fixed; the width follows the window's shape (see setFieldWidth),
 * so these are shared and change on resize.
 */
export const FIELD = { w: 384, h: 448 };
/** Where the enemy lives when it isn't moving. */
export const BOSS = { x: 192, y: 352 };
export const PLAYER_START = { x: 192, y: 64 };

/** Sets the field width. Bullets already out keep their positions. */
export function setFieldWidth(w: number): void {
  FIELD.w = w;
  BOSS.x = w / 2;
  PLAYER_START.x = w / 2;
}

/** Rewind keeps a snapshot every this many ticks... */
const SNAPSHOT_EVERY = 15;
/** ...for this many ticks back. */
const HISTORY_TICKS = 600;

/** What the host feeds the VM for one tick. Rewind replays these exactly. */
export interface Inputs {
  /** The player ship: the target `(aim)` points at. */
  tx: number;
  ty: number;
  vx: number;
  vy: number;
  /** The field width this tick. It sets the cull bounds and the field-w input. */
  w: number;
  /** The enemy the host moves, and where it puts it this tick. */
  enemy: { id: number; x: number; y: number } | null;
  /**
   * Named inputs changed this tick (rank, difficulty, var1 to var8): a value to set, or null to
   * unset. Usually empty, since inputs are sticky.
   */
  named: [string, number | null][];
  /** A beat clock change this tick: [bpm, offset in ticks], or null to turn it off. */
  beat?: [number, number] | null;
  /** Tags to cancel this tick, [] for every cancellable bullet (the bomb). */
  cancel?: string[];
}

interface Snapshot {
  tick: number;
  vm: Vm;
}

export class Sim {
  vm: Vm;
  seed: number;
  /** Bumped whenever the VM is replaced by a fresh one, so caches keyed on symbol ids reset. */
  generation = 0;
  /**
   * Called with the events from every live tick and host command (not from rewind replays), and
   * how many the queue dropped before them.
   */
  onEvents: (events: VmEvent[], dropped: number) => void = () => {};
  /** Ticks the VM itself took, for the stats readout. */
  tickMs = 0;

  private snapshots: Snapshot[] = [];
  private inputs = new Map<number, Inputs>();
  /** The newest tick reached live. After a rewind, `tick` is behind it. */
  private head = 0;

  private constructor(seed: number) {
    this.seed = seed;
    this.vm = this.freshVm();
  }

  static async create(seed: number): Promise<Sim> {
    await init();
    return new Sim(seed);
  }

  get tick(): number {
    return this.vm.tickCount();
  }

  get oldestTick(): number {
    return this.snapshots.length > 0 ? this.snapshots[0].tick : this.tick;
  }

  get newestTick(): number {
    return this.head;
  }

  /** Replaces the VM with a new one using `seed`. Definitions are gone afterwards. */
  reset(seed: number): void {
    this.seed = seed;
    this.vm.free();
    this.vm = this.freshVm();
    this.generation++;
    this.restartHistory();
  }

  load(src: string): LoadResult {
    const result = this.vm.load(src);
    this.afterCommand();
    return result;
  }

  eval(src: string, x: number, y: number): EvalResult {
    const result = this.vm.eval(src, x, y);
    this.afterCommand();
    return result;
  }

  spawn(name: string, x: number, y: number, kind: string): SpawnResult {
    const result = this.vm.spawn(name, x, y, new Float64Array(), kind);
    this.afterCommand();
    return result;
  }

  /**
   * Sets (or with null, unsets) a named input right away, outside the tick log. For a fresh VM
   * picking up the rank and vars already set; changes during play go through Inputs.named.
   */
  setVarNow(name: string, value: number | null): void {
    setVar(this.vm, name, value);
    this.afterCommand();
  }

  /** Sets the beat clock right away, outside the tick log, e.g. for a fresh VM. */
  setBeatNow(beat: [number, number] | null): void {
    setBeat(this.vm, beat);
    this.afterCommand();
  }

  kill(id: number): void {
    this.vm.kill(id);
    this.afterCommand();
  }

  names(): { name: string; arity: number }[] {
    return this.vm.names();
  }

  /** Runs one live tick. If we'd rewound, the future we'd recorded is dropped first. */
  step(inputs: Inputs): void {
    const t = this.tick;
    if (t < this.head) this.truncateAfter(t);
    if (t % SNAPSHOT_EVERY === 0 && this.snapshots.at(-1)?.tick !== t) {
      this.snapshots.push({ tick: t, vm: this.vm.snapshot() });
    }
    this.inputs.set(t, inputs);

    const start = performance.now();
    this.apply(inputs);
    this.tickMs = performance.now() - start;

    this.head = this.tick;
    this.drain();
    this.trimHistory();
  }

  /** Moves to tick `target` inside the history by restoring a snapshot and re-running inputs. */
  seek(target: number): void {
    const t = Math.max(this.oldestTick, Math.min(target, this.head));
    if (t === this.tick) return;
    let snap: Snapshot | undefined;
    for (const s of this.snapshots) if (s.tick <= t) snap = s;
    if (!snap) return;
    if (!(t > this.tick && this.tick >= snap.tick)) {
      this.vm.free();
      this.vm = snap.vm.snapshot();
    }
    while (this.tick < t) {
      const input = this.inputs.get(this.tick);
      if (!input) break;
      this.apply(input);
      this.vm.clearEvents();
    }
  }

  /** This tick's bullets, as views into VM memory: read them before the VM next changes. */
  bullets(): Bullets {
    return this.vm.bullets();
  }

  /** This tick's lasers, as views like `bullets`. */
  lasers(): Lasers {
    return this.vm.lasers();
  }

  /** The inputs for the tick that started at `t`, if it's still in the history. */
  recorded(t: number): Inputs | undefined {
    return this.inputs.get(t);
  }

  /** The simulation version shares record, from the core. */
  simVersion(): number {
    return simVersion();
  }

  kindName(kind: number): string {
    return this.vm.kindName(kind);
  }

  private apply(inputs: Inputs): void {
    for (const [name, value] of inputs.named) setVar(this.vm, name, value);
    if (inputs.beat !== undefined) setBeat(this.vm, inputs.beat);
    if (inputs.cancel) this.vm.cancel(inputs.cancel);
    // Inputs and the target are sticky, but setting the field size every tick from the log
    // keeps a replay exact even across a resize.
    setField(this.vm, inputs.w, FIELD.h);
    this.vm.setTarget(inputs.tx, inputs.ty, inputs.vx, inputs.vy);
    const e = inputs.enemy;
    if (e) this.vm.setPosition(e.id, e.x, e.y);
    this.vm.tick();
  }

  private freshVm(): Vm {
    const vm = new Vm(this.seed);
    prepare(vm, FIELD.w, FIELD.h);
    return vm;
  }

  /** Hands the queued events to onEvents and clears them. */
  private drain(): void {
    const events = this.vm.events();
    const dropped = this.vm.eventsDropped();
    this.vm.clearEvents();
    this.onEvents(events, dropped);
  }

  /** Host commands aren't recorded, so a rewind can't cross one. Start the history again here. */
  private afterCommand(): void {
    this.drain();
    this.restartHistory();
  }

  private restartHistory(): void {
    for (const s of this.snapshots) s.vm.free();
    this.snapshots = [{ tick: this.tick, vm: this.vm.snapshot() }];
    this.inputs.clear();
    this.head = this.tick;
  }

  private truncateAfter(t: number): void {
    for (const k of [...this.inputs.keys()]) if (k >= t) this.inputs.delete(k);
    this.snapshots = this.snapshots.filter((s) => {
      if (s.tick > t) s.vm.free();
      return s.tick <= t;
    });
  }

  private trimHistory(): void {
    const keepFrom = this.tick - HISTORY_TICKS;
    while (this.snapshots.length > 1 && this.snapshots[1].tick <= keepFrom) {
      this.snapshots.shift()?.vm.free();
    }
    const oldest = this.snapshots[0].tick;
    for (const k of this.inputs.keys()) {
      if (k >= oldest) break;
      this.inputs.delete(k);
    }
  }
}
